import type { StateKey } from '@/api'
import type { Device, DeviceFilterOption, FilterOptionsColumnPayload } from '@/api/deviceApi'
import { isDerivedStateKey, isStreamBackedStateKey } from '@/api/stateKeysApi'
import type { Stream, StreamField } from '@/api/streamsApi'
import { isDefaultField } from '@/app/pages/Streams/field-utils'
import type { DeviceStateField, Metadata } from '@/components/display-section/DisplaySection'
import { DEVICE_STATUS_COLUMN_NAME } from '@/config/deviceStatus'
import { capitalizeFirstLetter } from '@/dashboard-panels/common/utils/formatting'
import { isNumericFieldType } from '@/utils/clickhouseTypes'

/**
 * A live/filtered device list can shrink between when a page is requested and when the response
 * lands (e.g. a heartbeat filter losing matches, devices going inactive), leaving `page` past the
 * real last page. Callers should snap back to `totalPages` and retry instead of committing an
 * empty result for a page that no longer exists.
 */
export function isPageBeyondTotal(page: number, count: number, totalPages: number): boolean {
	return count > 0 && page > totalPages
}

/**
 * Extract available metadata fields from devices for display/filter options.
 */
export function getAvailableMetadata(devices: Device[]): Metadata[] {
	const metadataKeys = new Set<string>()
	for (const device of devices) {
		for (const key of Object.keys(device.metadata || {})) {
			metadataKeys.add(key)
		}
	}
	return Array.from(metadataKeys).map(key => ({
		id: key,
		label: capitalizeFirstLetter(key ?? ''),
	}))
}

/** `<key>_timestamp` companions and backend-injected stream default columns (id, timestamp,
 *  insert_timestamp — see `DEFAULT_FIELD_NAMES`) are bookkeeping, not real device-state fields —
 *  never shown or filtered on. `heartbeat` is a real device-state column and is unaffected. */
export const isStateFieldHidden = (name: string): boolean =>
	name.startsWith('.') || name.endsWith('_timestamp') || isDefaultField(name)

export function getAvailableStateFields(stateKeys: StateKey[]): DeviceStateField[] {
	const fields: DeviceStateField[] = [{ id: 'heartbeat', label: 'Heartbeat' }]
	// The reserved `status` column drives the device status icon, so it sits next to heartbeat rather
	// than wherever the state-keys API happened to return it.
	let statusField: DeviceStateField | null = null
	for (const stateKey of stateKeys) {
		if (stateKey.display_name === 'heartbeat' || isStateFieldHidden(stateKey.display_name)) {
			continue
		}
		const field: DeviceStateField = {
			id: stateKey.display_name,
			label: capitalizeFirstLetter(stateKey.display_name),
			source: isStreamBackedStateKey(stateKey)
				? { stream: stateKey.stream_name, field: stateKey.field_name }
				: undefined,
		}
		if (stateKey.display_name === DEVICE_STATUS_COLUMN_NAME) {
			statusField = field
			continue
		}
		fields.push(field)
	}
	if (statusField) {
		fields.splice(1, 0, statusField)
	}
	return fields
}

/**
 * Extract exportable state fields for CSV download (POST /v3/devices/download).
 * Excludes reserved columns (like 'status') and derived states, which do not exist
 * as physical columns in the Postgres `{tenant}_state` table.
 */
export function getExportableStateFields(stateKeys: StateKey[]): Array<{ id: string; label: string }> {
	const fields = [{ id: 'heartbeat', label: 'Heartbeat' }]
	for (const stateKey of stateKeys) {
		if (
			stateKey.display_name === 'heartbeat' ||
			stateKey.display_name === DEVICE_STATUS_COLUMN_NAME ||
			isDerivedStateKey(stateKey) ||
			isStateFieldHidden(stateKey.display_name)
		) {
			continue
		}
		fields.push({
			id: stateKey.display_name,
			label: capitalizeFirstLetter(stateKey.display_name),
		})
	}
	return fields
}

/**
 * Stands in for a derived column's type: the backend stores its states as opaque JSON and reports no type.
 * Deliberately not a valid clickhouse type name, so it cannot collide with a real stream field type.
 */
export const DERIVED_FIELD_TYPE = 'Derived'

export const isDerivedFieldType = (type: string | undefined): boolean => type === DERIVED_FIELD_TYPE

/**
 * Resolve a device state field's source schema (which carries its clickhouse `type`).
 * Prefers the hydrated stream schema and falls back to the state key's inline schema.
 */
export function getDeviceStateFieldInfo(
	streams: Stream[] | undefined,
	stateKeys: StateKey[],
	fieldName: string,
): StreamField | undefined {
	const stateKey = stateKeys.find(sk => sk.display_name.toLowerCase() === fieldName.toLowerCase())
	if (stateKey) {
		if (isDerivedStateKey(stateKey)) {
			return { name: stateKey.display_name, type: DERIVED_FIELD_TYPE, formatter: null }
		}
		const resolved = streams
			?.find(s => s.name === stateKey.stream_name)
			?.fields?.find(f => f.name.toLowerCase() === stateKey.field_name?.toLowerCase())
		if (resolved) {
			return resolved
		}
		return {
			name: stateKey.field_name,
			type: stateKey.type,
			formatter: stateKey.formatter,
		}
	}
	for (const stream of streams ?? []) {
		const found = stream.fields?.find(f => f.name.toLowerCase() === fieldName.toLowerCase())
		if (found) {
			return found
		}
	}
	return undefined
}

/** `isNumericFieldType` against a device state field's resolved stream schema. */
export function isNumericStateField(streams: Stream[] | undefined, stateKeys: StateKey[], fieldName: string): boolean {
	return isNumericFieldType(getDeviceStateFieldInfo(streams, stateKeys, fieldName)?.type)
}

export function getAvailableMetadataFromFilterOptions(options: DeviceFilterOption[]): Metadata[] {
	return options
		.filter(option => option.kind === 'metadata')
		.map(option => ({
			id: option.filterName,
			label: capitalizeFirstLetter(option.filterName ?? ''),
		}))
}

/** Map a search-bar column id to the filter-options `column` payload. Returns null for columns the
 *  endpoint can't paginate (project/action/status/boolean/timestamp) — caller uses client suggestions. */
export function deviceColumnIdToFilterColumn(columnId: string): FilterOptionsColumnPayload | null {
	// 'deviceId' is Remote Commands' and 'device_id' the fleet dashboard's search-bar column id for the
	// device serial key.
	if (columnId === 'id' || columnId === 'deviceId' || columnId === 'device_id') {
		return { kind: 'id' }
	}
	if (columnId === 'uid') {
		return { kind: 'uid' }
	}
	if (columnId.startsWith('metadata.')) {
		const name = columnId.slice('metadata.'.length)
		return name ? { kind: 'metadata', name } : null
	}
	if (columnId.startsWith('state.')) {
		const name = columnId.slice('state.'.length)
		// state.heartbeat / *_timestamp are rejected by the backend; skip them.
		if (!name || name === 'heartbeat' || name.endsWith('_timestamp')) {
			return null
		}
		return { kind: 'state', name }
	}
	return null
}

export function buildDeviceColumnValueOptions(options: DeviceFilterOption[]): Record<string, (string | number)[]> {
	const columnValueOptions: Record<string, (string | number)[]> = {}

	for (const option of options) {
		if (option.kind === 'id') {
			columnValueOptions.id = option.filterValues
		} else if (option.kind === 'uid') {
			columnValueOptions.uid = option.filterValues
		} else if (option.kind === 'metadata') {
			columnValueOptions[`metadata.${option.filterName}`] = option.filterValues
		} else if (option.kind === 'state') {
			columnValueOptions[`state.${option.filterName}`] = option.filterValues
		} else if (option.kind === 'project') {
			columnValueOptions.tenantName = option.filterValues
		}
	}

	return columnValueOptions
}
